import Layout from "../../../common/components/layout";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getUser} from "../../../common/util/util-auth";
import {Field, FieldsManager} from "../../../data/services/fields";
import {DEFAULT_HISTORY_TABLE_FILTER_FIELDS, DEFAULT_QUERY_LIMIT} from "../../../common/util/util-consts";
import ResourcePage from "../../../common/components/layout/layout-components/resource-page";
import Resources from "../../../data/services/resources";
import {getProp} from "../../../common/util/util-helpers";
import useQueryNew from "../../../hooks/use-query-new";
import {createResource, deleteResource, getResource, updateResource} from "../../../data/actions/resource";
import {showModal} from "../../../data/actions/ui";
import ModalConfirm from "../../../common/components/modal/modal-confirm";
import {useLocation} from "react-router-dom";
import LocalStorage from "../../../common/util/localStorage";
import ContactForm from "../../../common/components/forms/contact-form";
import Modal from "../../../common/components/modal";
import HistoryModal, {DEFAULT_HISTORY_TABLE_FIELDS} from "../../../common/components/modal/modal-history";
import {getHistoryResource} from "../../../data/actions/historyResource";
import {getContactResource} from "../../../data/actions/contactResource";

export default function ContactsView({translate, match, history}) {
    /** Page constants
     ================================================================= */
    const pageTitle = translate('page.heading.contacts');
    const tableKey = 'ContactID';
    const noRecordsText = "Create new contact";
    const noRecordsButtonLabel = translate("text.create_contact");
    const resourcePath = Resources.Contacts;
    const tableDefaults = {
        behaviour: {
            rowSelect: true,
            canAdjustWidth: true,
            hasMenu: true
        },
        style: {
            condensed: true,
            isGPUAccelerated: true
        },
        version: "1" // changing version will force update tableDefaults settings
    }


    const location = useLocation();
    const pagePath = location.pathname.replaceAll("/", "");


    const user = getUser();
    const dispatch = useDispatch();
    const ui = useSelector(state => state.ui);
    const resource = useSelector(state => state.resource);

    const contactResource = useSelector(state => state.contactResource);
    const isContactLoading = contactResource.isLoading;
    const contactData = contactResource?.data ?? null;

    const historyResource = useSelector(state => state.historyResource);

    const [breakpoint, setBreakpoint] = useState({});
    const [isContactFormModalOpen, setIsContactFormModalOpen] = useState(false);
    const [emailExist, setEmailExist] = useState(false);
    const [queryFields, setQueryFields] = useQueryNew(getQueryFields(), pagePath);
    const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
    const [isConfirmRestoreModalOpen, setIsConfirmRestoreModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const query = FieldsManager.getFieldKeyValues(queryFields);

    const [selectedItem, setSelectedItem] = useState(null);
    const [prefilled, setPrefilled] = useState({});

    /** Data Events
     ================================================================= */
    function fetchData() {
        dispatch(getResource({
            user: user,
            resource: resourcePath,
            query: query
        }))
    }

    function fetchContactData() {
        if (!selectedItem) {
            return false
        }

        dispatch(getContactResource({
            user: LocalStorage.get('user'),
            resource: Resources.Contacts,
            query: {id: selectedItem.ContactID}
        }))
    }

    function submitContactForm(params, file, documents = []) {
        if (selectedItem?.ContactID) {
            dispatch(updateResource({
                user: user,
                params: params,
                errorMessage: true,
                successMessage: translate('message.ContactUpdatedSuccessfully'),
                query: query,
                resource: resourcePath,
                piggyResource: resourcePath,
                file: !!file && [file],
                fileResource: Resources.ContactImage,
                document: documents,
                documentResource: Resources.ContactDocuments
            }));

            handleCloseContactFormModalClick();
        } else {
            dispatch(createResource({
                user: LocalStorage.get('user'),
                params: params,
                errorMessage: true,
                successMessage: translate('message.ContactCreatedSuccessfully'),
                query: query,
                resource: resourcePath,
                piggyResource: resourcePath,
                file: !!file && [file],
                fileResource: Resources.ContactImage,
                document: documents,
                documentResource: Resources.ContactDocuments
            }));
        }
    }

    /** UI Events
     ================================================================= */
    function handleBreakpointChange(breakpointUpdate) {
        setBreakpoint(Object.assign({}, breakpointUpdate));

        if (breakpointUpdate.index <= 1 && queryFields.limit.value !== 10) {
            handleQueryInputChange('limit', 10);
        }
    }

    function handleCreateResourceClick() {
        setSelectedItem({});
        setIsContactFormModalOpen(true);
    }

    function handleQueryInputChange(name, value) {
        const queryFieldsUpdate = FieldsManager.updateField(queryFields, name, value);

        if ("sortBy" === name) {
            if (value === queryFieldsUpdate?.sortBy?.value) {
                queryFieldsUpdate.sort.value = queryFieldsUpdate.sort.value === 'ASC' ? 'DESC' : 'ASC'
            }
        }

        if ("query" === name) {
            queryFieldsUpdate.offset.value = 0;
        }

        setQueryFields(queryFieldsUpdate);
    }


    function handleClearAllFiltersClick() {
        setQueryFields(getQueryFields());
    }

    function handleViewDetails(item) {
        dispatch(showModal('ViewContactCard', {ContactID: item.ContactID}))
    }

    function handleEditClick(item = null) {
        setSelectedItem(item);
        setIsContactFormModalOpen(true)
    }

    function handleCloseContactFormModalClick() {
        setSelectedItem(null);
        setIsContactFormModalOpen(false);
        setEmailExist(false)
    }

    function handleDeleteResourceClick(item) {
        setSelectedItem(item);
        setIsConfirmDeleteModalOpen(true);
    }

    function handleRestoreResourceClick(item) {
        setSelectedItem(item);
        setIsConfirmRestoreModalOpen(true);
    }

    function handleConfirmDeleteClick() {
        dispatch(deleteResource({
            user: user,
            query: Object.assign({
                ContactID: selectedItem.ContactID
            }, query),
            errorMessage: true,
            successMessage: translate(`message.contact_archived`, [selectedItem.FirstName, selectedItem.LastName]),
            resource: resourcePath,
            piggyResource: resourcePath
        }));
        setSelectedItem({});
        setIsConfirmDeleteModalOpen(false);
    }

    function handleConfirmRestoreClick() {
        dispatch(updateResource({
            user: user,
            params: {
                id: selectedItem.ContactID,
                ArchivedDate: 1
            },
            query: query,
            errorMessage: true,
            successMessage: `Contact ${selectedItem.FirstName} ${selectedItem.LastName}  restored`,
            resource: resourcePath,
            piggyResource: resourcePath
        }));
        setSelectedItem(null);
        setIsConfirmRestoreModalOpen(false);
    }

    function showContactDialog() {
        const query = new URLSearchParams(location.search)
        const ContactID = query.get('ContactID')
        if (ContactID) {
            handleEditClick({ContactID: ContactID});
        }
    }

    /** Helpers
     ================================================================= */
    function getQueryFields() {
        return {
            query: new Field('query', '', [''], false, 'search'),
            Groups: new Field('Groups', '', [''], false, 'select-search', {}, {
                isClearable: true
            }),
            ContactType: new Field('ContactType', '', [''], false, 'select'),
            HighImportance: new Field('HighImportance', '', [''], false, 'checkbox'),
            archived: new Field('archived', '', [''], false, 'checkbox'),
            sort: new Field('sort', 'ASC', ['']),
            sortBy: new Field('sortBy', 'FirstName', ['']),
            offset: new Field('offset', '0', ['']),
            limit: new Field('limit', DEFAULT_QUERY_LIMIT, [''], false, 'select', {hideLabel: true}, {
                menuPlacement: "top",
                hasPortal: true
            })
        }
    }

    function getFields() {
        return {
            FirstName: new Field('FirstName', '', ['empty']),
            LastName: new Field('LastName', '', ['empty']),
            Nickname: new Field('Nickname', '', ['']),
            Email: new Field('Email', '', ['empty', 'email'], false, 'email'),
            PrivateEmail: new Field('PrivateEmail', '', ['empty', 'email'], false, 'email'),
            DateOfBirth: new Field('DateOfBirth', '', [], false, 'date'),
            CreateUpdateDate: new Field('CreateUpdateDate', '', [], false, 'date')
        }
    }

    function getFieldTypes() {
        let fieldsCombined = Object.assign({}, getFields())

        fieldsCombined = Object.values(fieldsCombined).reduce((memo, it) => {
            memo[it.name] = it.type
            return memo
        }, {})

        fieldsCombined['HighImportance'] = 'checkbox'
        fieldsCombined['IsSystemUser'] = 'checkbox'
        fieldsCombined['DateOfBirth'] = 'date'

        return fieldsCombined;
    }

    useEffect(() => {
        fetchData();
        showContactDialog()
    }, [queryFields]);

    useEffect(() => {
        // Todo: remove in the next version
        function consumeLegacyState() {
            const oldState = LocalStorage.get(pagePath + "_state");
            if (oldState) {
                const queryFieldsUpdate = Object.assign({}, queryFields);
                queryFieldsUpdate.sort.value = oldState.sort;
                queryFieldsUpdate.sortBy.value = oldState.sortBy;
                queryFieldsUpdate.limit.value = oldState.limit;
                queryFieldsUpdate.offset.value = oldState.offset;
                queryFieldsUpdate.query.value = oldState?.queryFilterFields?.query?.value ?? "";
                queryFieldsUpdate.Groups.value = oldState?.queryFilterFields?.Groups?.value ?? "";
                queryFieldsUpdate.ContactType.value = oldState?.queryFilterFields?.ContactType?.value ?? "";
                queryFieldsUpdate.HighImportance.value = oldState?.queryFilterFields?.HighImportance?.value ?? "";

                LocalStorage.remove(pagePath + "_state");
                setQueryFields(queryFieldsUpdate);
            }
        }

        consumeLegacyState();
    }, [pagePath]);

    useEffect(() => {
        if (resource.create?.id) {
            setIsContactFormModalOpen(false);
        }
    }, [resource])

    /** Render
     ================================================================= */
    return (
        <Layout
            ui={ui}
            user={user}
            match={match}
            history={history}
            location={location}
            translate={translate}
            dispatch={dispatch}
            isAccessible={!(resource.errorStatus === 2)}
            onBreakpointChange={handleBreakpointChange}
        >
            <ResourcePage
                tableKey={tableKey}
                pagePath={pagePath}
                resourcePath={resourcePath}
                pageTitle={pageTitle}
                tableFields={getFields()}
                onCreateResourceClick={handleCreateResourceClick}
                onNoRecordsCreateClick={() => {
                    if (queryFields.query.value) {
                        const firstSpaceIndex = queryFields.query.value.indexOf(' ');
                        let firstName = '';
                        let lastName = '';

                        if (firstSpaceIndex !== -1) {
                            firstName = queryFields.query.value.slice(0, firstSpaceIndex);
                            lastName = queryFields.query.value.slice(firstSpaceIndex + 1);
                        } else {
                            firstName = queryFields.query.value;
                        }

                        setPrefilled({FirstName: firstName, LastName: lastName});
                    }

                    setIsContactFormModalOpen(true);
                }}
                queryFields={queryFields}
                onQueryInputChange={handleQueryInputChange}
                onClearAllFiltersClick={handleClearAllFiltersClick}
                fetchData={fetchData}
                translate={translate}
                breakpoint={breakpoint}
                onView={handleViewDetails}
                onEdit={handleEditClick}
                onDelete={handleDeleteResourceClick}
                onRestore={handleRestoreResourceClick}
                onRowClick={handleEditClick}
                tableDefaults={tableDefaults}
                noRecordsText={noRecordsText}
                noRecordsButtonLabel={noRecordsButtonLabel}
            />

            <Modal
                show={isContactFormModalOpen}
                widthClass={'max-w-3xl'}
                onClose={handleCloseContactFormModalClick}
            >
                <ContactForm
                    data={!!selectedItem?.ContactID && contactData}
                    isLoading={isContactLoading}
                    onMount={fetchContactData}
                    onClose={handleCloseContactFormModalClick}
                    errorMessages={emailExist ? [{field: 'Email', message: 'text.email_exists'}] : []}
                    prefilled={prefilled}
                    clearErrorData={() => setEmailExist(false)}
                    onSubmit={submitContactForm}
                    onViewHistory={() => setIsHistoryModalOpen(true)}
                    translate={translate}
                    isEdit={!!selectedItem?.ContactID}
                    isSystemUser={false}
                />

                <Modal
                    show={isHistoryModalOpen}
                    widthClass={"max-w-5xl"}
                    onClose={() => setIsHistoryModalOpen(false)}
                    translate={translate}
                >
                    {isHistoryModalOpen && (
                        <HistoryModal
                            onClose={() => setIsHistoryModalOpen(false)}
                            translate={translate}
                            title={translate("text." + Resources.ContactsHistory)}
                            tableFields={DEFAULT_HISTORY_TABLE_FIELDS(translate, getFieldTypes())}
                            filterFields={DEFAULT_HISTORY_TABLE_FILTER_FIELDS()}
                            onFetchData={(query) => {
                                dispatch(getHistoryResource({
                                    user: user,
                                    query: Object.assign({}, query, {
                                        id: selectedItem.ContactID
                                    }),
                                    resource: Resources.ContactsHistory
                                }))
                            }}
                            data={getProp(historyResource, "data.list", [])}
                            count={getProp(historyResource, "data.count", 0)}
                            isLoading={getProp(historyResource, "isLoading", false)}
                            showUserAvatar

                            onRowClick={(item) => {
                                dispatch(showModal('ViewContactCard', {ContactID: item.ContactID}))
                            }}
                            fieldTypes={getFieldTypes()}
                        />
                    )}
                </Modal>
            </Modal>

            <ModalConfirm
                title={translate("modal_heading.confirm_restore")}
                show={isConfirmRestoreModalOpen}
                text={`${translate('message.are_you_sure_restore_contact')} ${selectedItem?.FirstName} ${selectedItem?.LastName}?`}
                onClose={() => {
                    setIsConfirmRestoreModalOpen(false);
                    setSelectedItem(null);
                }}
                buttonLabel={translate('btn.confirm')}
                closeButtonLabel={'Cancel'}
                translate={translate}
                onConfirm={handleConfirmRestoreClick}
            />

            <ModalConfirm
                title={'Confirm'}
                show={isConfirmDeleteModalOpen}
                text={`${translate('message.confirm_archive_generic')} ${selectedItem?.FirstName} ${selectedItem?.LastName}?`}
                onClose={() => {
                    setIsConfirmDeleteModalOpen(false);
                    setSelectedItem({});
                }}
                buttonLabel={translate('btn.confirm')}
                closeButtonLabel={'Cancel'}
                translate={translate}
                onConfirm={handleConfirmDeleteClick}
            />
        </Layout>
    )
}